import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import type { EscalationStep, NotificationChannel } from '../lib/types'
import { ErrorBox, Loading } from './Feedback'

interface Entwurf {
  after_minutes: number
  channel_id: string
  only_critical: boolean
}

const MAX_STUFEN = 5

/** Prüft die Liste wie die Zentrale, damit der Fehler vor dem Speichern steht. */
export function stufenFehler(stufen: Entwurf[]): string | null {
  let vorher = 0
  for (const [i, s] of stufen.entries()) {
    if (!s.channel_id) return `Stufe ${i + 1}: Kanal wählen.`
    if (!Number.isInteger(s.after_minutes) || s.after_minutes < 5 || s.after_minutes > 1440) {
      return `Stufe ${i + 1}: die Frist liegt zwischen 5 und 1440 Minuten.`
    }
    if (s.after_minutes <= vorher) return `Stufe ${i + 1} muss später kommen als Stufe ${i}.`
    vorher = s.after_minutes
  }
  return null
}

/**
 * Alarm-Eskalation: ein Alarm, den nach der Frist niemand bestätigt hat,
 * geht zusätzlich an den Kanal der Stufe – etwa an die zweite Bereitschaft
 * oder die Teamleitung. Bestätigen beendet die Eskalation.
 */
export function Eskalation() {
  const queryClient = useQueryClient()
  const stufen = useQuery({
    queryKey: ['eskalation'],
    queryFn: ({ signal }) => apiGet<EscalationStep[]>('/eskalation', signal),
  })
  const kanaele = useQuery({
    queryKey: ['channels'],
    queryFn: ({ signal }) =>
      apiGet<{ channels: NotificationChannel[]; outbox: { pending: number; failed: number } }>('/channels', signal),
  })
  const [entwurf, setEntwurf] = useState<Entwurf[] | null>(null)
  useEffect(() => {
    if (stufen.data && entwurf === null) {
      setEntwurf(
        stufen.data.map((s) => ({
          after_minutes: s.after_minutes,
          channel_id: s.channel_id,
          only_critical: s.only_critical,
        })),
      )
    }
  }, [stufen.data, entwurf])
  const speichern = useMutation({
    mutationFn: (liste: Entwurf[]) => apiSend<EscalationStep[]>('PUT', '/eskalation', { stufen: liste }),
    onSuccess: (neu) => {
      queryClient.setQueryData(['eskalation'], neu)
      setEntwurf(null)
    },
  })

  if (stufen.isError) return <ErrorBox error={stufen.error} what="Die Eskalation" />
  if (kanaele.isError) return <ErrorBox error={kanaele.error} what="Die Kanäle" />
  if (stufen.isPending || kanaele.isPending || entwurf === null) return <Loading what="Eskalation" />

  const liste = kanaele.data.channels
  const fehler = stufenFehler(entwurf)
  const aendern = (i: number, teil: Partial<Entwurf>) =>
    setEntwurf(entwurf.map((s, j) => (j === i ? { ...s, ...teil } : s)))

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Eskalation</h2>
      </div>
      <div className="card-body stack" style={{ gap: 12 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Hat nach der Frist niemand einen Alarm bestätigt, geht er zusätzlich an den Kanal der Stufe. Die Frist zählt
          ab der ersten Benachrichtigung. Bestätigte, unterdrückte und im Wartungsfenster liegende Alarme eskalieren
          nicht.
        </p>
        {entwurf.length === 0 ? <span className="muted">Keine Eskalation eingerichtet.</span> : null}
        {entwurf.map((s, i) => (
          <div key={i} className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <strong style={{ width: 70 }}>Stufe {i + 1}</strong>
            <label className="row" style={{ gap: 6 }}>
              nach
              <input
                className="input"
                type="number"
                min={5}
                max={1440}
                style={{ width: 80 }}
                aria-label={`Frist Stufe ${i + 1} in Minuten`}
                value={s.after_minutes}
                onChange={(e) => aendern(i, { after_minutes: Number(e.target.value) })}
              />
              Minuten an
            </label>
            <select
              className="input"
              aria-label={`Kanal Stufe ${i + 1}`}
              value={s.channel_id}
              onChange={(e) => aendern(i, { channel_id: e.target.value })}
            >
              <option value="">Kanal wählen …</option>
              {liste.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.enabled ? '' : ' (aus)'}
                </option>
              ))}
            </select>
            <label className="row" style={{ gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={s.only_critical}
                onChange={(e) => aendern(i, { only_critical: e.target.checked })}
              />
              nur kritische Alarme
            </label>
            <button type="button" className="btn btn-sm" onClick={() => setEntwurf(entwurf.filter((_, j) => j !== i))}>
              Entfernen
            </button>
          </div>
        ))}
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm"
            disabled={entwurf.length >= MAX_STUFEN || liste.length === 0}
            onClick={() =>
              setEntwurf([
                ...entwurf,
                {
                  after_minutes: (entwurf[entwurf.length - 1]?.after_minutes ?? 0) + 15,
                  channel_id: '',
                  only_critical: false,
                },
              ])
            }
          >
            Stufe hinzufügen
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={fehler !== null || speichern.isPending}
            onClick={() => speichern.mutate(entwurf)}
          >
            Eskalation speichern
          </button>
          {liste.length === 0 ? <span className="muted">Erst unter Benachrichtigung einen Kanal anlegen.</span> : null}
        </div>
        {fehler ? <span style={{ color: 'var(--warn)', fontSize: 13 }}>{fehler}</span> : null}
        {speichern.isError ? (
          <span style={{ color: 'var(--crit)', fontSize: 13 }}>{(speichern.error as Error).message}</span>
        ) : null}
        {speichern.isSuccess ? <span className="muted">Gespeichert.</span> : null}
      </div>
    </section>
  )
}
